import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {
  Conversation,
  RequestACallDetails,
} from '@stripe-internal/conversations-client';

import type {Api} from '../../../core/lib/api/api';
import type {AppEntryPoint, AppVisitor} from '../../../core/types';
import {
  RequestACallCancelEndpointResponse,
  RequestACallCodesEndpointResponseSuccess,
} from '../../../generated/types';
import {useApi, useEntryPoint, useVisitor} from '../../../core/hooks/appHooks';
import {useConversationEnded} from './useConversationEnded';

type VerificationCodes = {
  agentCode: string;
  userCode: string;
};

type RacStatus = RequestACallCodesEndpointResponseSuccess['racStatus'];

const STATUS_POLLING_FREQUENCY_MS = 5_000; // 5 seconds

export type RACCancelFn = (
  entryPoint: AppEntryPoint,
  conversationId: string,
) => Promise<RequestACallCancelEndpointResponse>;

type RACDetailsDependencies = {
  api: Pick<Api, 'fetchRequestACallCodes' | 'cancelRequestACall'>;
  entryPoint: AppEntryPoint;
  visitor: AppVisitor;
  // Overrides the RAC-cancel call. Threads app routes cancellation through
  // the generic conversation-ending endpoint instead of the normal app's
  // dedicated RAC-cancel endpoint.
  cancelRequestACallOverride?: RACCancelFn;
};

export function useRACDetailsForEnvironment(
  conversation: Conversation,
  {
    api,
    entryPoint,
    visitor,
    cancelRequestACallOverride,
  }: RACDetailsDependencies,
) {
  const {fetchRequestACallCodes} = api;
  const cancelRequestACall =
    cancelRequestACallOverride ?? api.cancelRequestACall;

  const [verificationCodes, setVerificationCodes] = useState<
    VerificationCodes | undefined
  >();

  const firstFetchRef = useRef<boolean>(false);

  const currTimeout = useRef<NodeJS.Timeout>();
  const racDoneStrikes = useRef<number>(0);
  const isInitialLoading = useRef<boolean>(true);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [racStatus, setRacStatus] = useState<RacStatus>('pending');
  const [isCancelLoading, setIsCancelLoading] = useState<boolean>(false);
  const [callEnded, setCallEnded] = useState<boolean>(false);

  const [racDetails, setRacDetails] = useState<RequestACallDetails | undefined>(
    conversation?.context.requestACallDetails,
  );

  const hasVerificationCodes = useMemo(
    () => visitor.sessionType !== 'anonymous',
    [visitor],
  );

  const conversationEnded = useConversationEnded(conversation);

  useEffect(() => {
    if (!conversation) {
      return;
    }

    const sub = conversation
      .contextObservable()
      .subscribe(() => setRacDetails(conversation.context.requestACallDetails));
    return () => sub.unsubscribe();
  }, [conversation]);

  const userName: string = useMemo(
    () => racDetails?.userName || visitor.displayName || '',
    [racDetails, visitor],
  );

  const userPhoneNumber: string = useMemo(
    () => racDetails?.userPhoneNumber || visitor.phoneNumber || '',
    [racDetails, visitor],
  );

  const fetchCodes = useCallback(async () => {
    setLoading(true);

    fetchRequestACallCodes(entryPoint, conversation.id)
      .then((resp) => {
        if (conversationEnded) {
          return;
        }

        if (resp.type === 'success') {
          if (!resp.pending) {
            racDoneStrikes.current += 1;

            if (racDoneStrikes.current >= 2) {
              racDoneStrikes.current = 0;
              setCallEnded(true);
              return;
            }
          } else if (hasVerificationCodes) {
            setVerificationCodes({
              agentCode: resp.agentCode || '',
              userCode: resp.userCode || '',
            });
          }

          isInitialLoading.current = false;
          setRacStatus(resp.racStatus ?? 'calling');

          const nextReloadInMs = resp.nextReload
            ? resp.nextReload * 1000
            : STATUS_POLLING_FREQUENCY_MS;

          const nextTimeout = Math.min(
            nextReloadInMs,
            STATUS_POLLING_FREQUENCY_MS,
          );
          currTimeout.current = setTimeout(fetchCodes, nextTimeout);
        } else {
          throw new Error('Unexpected RAC status response');
        }
      })
      .catch(() => {
        setLoading(false);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [
    conversationEnded,
    entryPoint,
    fetchRequestACallCodes,
    conversation.id,
    hasVerificationCodes,
  ]);

  useEffect(() => {
    // not using useMountEffect
    // https://trailhead.corp.stripe.com/docs/dashboard/developing-in-dashboard/no-new-use-mount-effect
    if (firstFetchRef.current) {
      return;
    }

    firstFetchRef.current = true;
    fetchCodes();
    return () => {
      return clearTimeout(currTimeout.current);
    };
  });

  useEffect(() => {
    if (conversationEnded) {
      clearTimeout(currTimeout.current);
    }
  }, [conversationEnded]);

  const cancelRac = useCallback(async () => {
    setIsCancelLoading(true);
    cancelRequestACall(entryPoint, conversation.id)
      .then((resp) => {
        if (resp.type === 'success') {
          setCallEnded(true);
          conversation?.setContextValues({
            ...conversation.context,
            requestACallDetails: {
              userName,
              userPhoneNumber,
              callCanceled: true,
            },
          });
        } else if (resp.type === 'conflict' || resp.type === 'not-found') {
          setIsCancelLoading(false);
          setRacStatus('pending');
        } else {
          throw new Error('Unexpected RAC cancel response');
        }
      })
      .catch(() => {
        setIsCancelLoading(false);
        setRacStatus('pending');
      });
  }, [cancelRequestACall, entryPoint, conversation, userName, userPhoneNumber]);

  return {
    userName,
    userPhoneNumber,
    error,
    loading,
    hasVerificationCodes,
    verificationCodes,
    isInitialLoading: isInitialLoading.current,
    racStatus: racStatus ?? 'calling',
    isCancelLoading: isCancelLoading || isInitialLoading.current,
    cancelRac,
    callEnded: conversationEnded || callEnded,
  };
}

export function useRACDetails(conversation: Conversation) {
  const entryPoint = useEntryPoint();
  const api = useApi();
  const visitor = useVisitor();

  return useRACDetailsForEnvironment(conversation, {
    api,
    entryPoint,
    visitor,
  });
}
